// The Translator — its rules, pure (no DOM, no React; tested under node by
// ../test/run-translator-tests.mjs).
//
// Stride speaks English. A site admin may switch the Translator on (Settings →
// Preferences); each person then picks a language with the dictionary icon
// beside the theme icon. It is a DISPLAY translator — an emulator: what the
// screen shows is looked up in a dictionary and shown in the chosen language;
// nothing that is stored, searched or sent changes (field names, statuses and
// ZQL stay English underneath), and English comes back the moment it is picked.
//
//   LANGUAGES / MAIN           the languages on offer (English is always the main one)
//   normalizeSetting(raw)      a site's setting — OFF unless an admin switched it on
//   compile(layers, lang)      dictionaries → one lookup (site terms first, then
//                              the client's, then the product's)
//   translate(text, c)         a rendered text → its translation, or null
//   translateDates(text, lang) month and weekday names next to a date

export const MAIN = { code: 'en', label: 'English', native: 'English' };
export const LANGUAGES = [
  { code: 'af', label: 'Afrikaans', native: 'Afrikaans' },
  { code: 'de', label: 'German', native: 'Deutsch' },
  { code: 'pl', label: 'Polish', native: 'Polski' },
];
export const CODES = LANGUAGES.map((l) => l.code);
export const languageOf = (code) => (code === 'en' ? MAIN : LANGUAGES.find((l) => l.code === code) || null);

const MAX_TERMS = 5000;
const MAX_LEN = 400;
const clean = (s) => String(s == null ? '' : s).replace(/\s+/g, ' ').trim().slice(0, MAX_LEN);

// The words from what a dictionary loader gave: an imported JSON module (its default), or the words
// themselves — "default" can be an English word with a translation, so only an OBJECT under it is
// the module's.
export const wordsOf = (m) => { const d = m && m.default && typeof m.default === 'object' ? m.default : m; return d && typeof d === 'object' ? d : {}; };

// A site's setting. Anything but an explicit `enabled: true` is OFF — a site
// that never saw this feature, a new site, a broken row: all OFF.
export function normalizeSetting(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const langs = Array.isArray(r.languages) ? [...new Set(r.languages.map(String).filter((c) => CODES.includes(c)))] : null;
  const glossary = {};
  let n = 0;
  if (r.glossary && typeof r.glossary === 'object') {
    for (const [en, tr] of Object.entries(r.glossary)) {
      const key = clean(en);
      if (!key || !tr || typeof tr !== 'object' || n >= MAX_TERMS) continue;
      const row = {};
      for (const c of CODES) { const v = clean(tr[c]); if (v) row[c] = v; }
      if (Object.keys(row).length) { glossary[key] = row; n += 1; }
    }
  }
  return {
    enabled: r.enabled === true,
    // the languages people may pick (all three unless the admin left some out)
    languages: langs && langs.length ? CODES.filter((c) => langs.includes(c)) : [...CODES],
    glossary,
  };
}

// A save MERGES: this admin's changes (draft against what was loaded) laid over the site's setting
// as it is NOW — another admin's terms saved meanwhile are kept.
export function mergeSetting(base, draft, now) {
  const out = { enabled: draft.enabled !== base.enabled ? draft.enabled : now.enabled, languages: JSON.stringify(draft.languages) !== JSON.stringify(base.languages) ? draft.languages : now.languages, glossary: { ...now.glossary } };
  const terms = new Set([...Object.keys(base.glossary), ...Object.keys(draft.glossary)]);
  for (const en of terms) {
    const b = base.glossary[en] || {}; const d = draft.glossary[en] || {};
    const row = { ...(out.glossary[en] || {}) };
    for (const code of CODES) {
      if ((b[code] || '') === (d[code] || '')) continue; // not changed here
      if (d[code]) row[code] = d[code]; else delete row[code];
    }
    if (Object.keys(row).length) out.glossary[en] = row; else delete out.glossary[en];
  }
  return out;
}


// ---- the lookup ---------------------------------------------------------------------------------
export const norm = (s) => String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const HOLE = /\{([a-zA-Z0-9_]+)\}/g;

// A hole is TYPED by its name, so a pattern never swallows people's words: {n}, {count}, {days} …
// match only a number; {key} only a work item key; any other hole ({name}, {x}) matches text —
// and a pattern whose text hole sits at its very start or end with fewer than two words of its own
// ("Open {x}", "{x} selected") is left out: it would rewrite a Summary that merely starts or ends
// the same way.
const NUM_HOLE = /^(n|count|num|number|total|done|of|max|min|i|k|days?|hours?|hrs|mins?|minutes?|seconds?|secs?|size|pct|percent|rows|items|version|page|pages|year|qty|amount|limit|left|more|shown|selected|age|weeks?|months?|width|height|level|index|step|steps)\d*$/i;
const KEY_HOLE = /^(key|issueKey|itemKey|wiKey|parentKey|childKey)\d*$/i;
const DATE_HOLE = /^(date|when|due|start|end|time|since|until)\d*$/i;
const holeRe = (name) => (NUM_HOLE.test(name) ? '(-?\\d[\\d\\s.,]*%?)' : KEY_HOLE.test(name) ? '([A-Z][A-Z0-9_]*-\\d+)' : DATE_HOLE.test(name) ? `(${DATE_CORE}|\\d{4}-\\d{2}-\\d{2}|\\d{1,2}[/.]\\d{1,2}[/.]\\d{2,4})` : '(.+?)');
const isTextHole = (name) => !NUM_HOLE.test(name) && !KEY_HOLE.test(name) && !DATE_HOLE.test(name);
// a hole that carries what people wrote — a Summary, a name, a value, a search — keeps it exactly as
// written, even when it happens to be a dictionary phrase ("Delivery", "Done"); other holes ("Status:
// {x}") show a known phrase in the language
const PEOPLE_HOLE = /^(name|summary|title|label|user|who|person|assignee|reporter|owner|project|group|team|value|q|query|text|comment|email|customer|client|supplier|file|filename|url|site)\d*$/i;
const holeValue = (v, name, c, lang) => {
  if (PEOPLE_HOLE.test(name)) return v;
  return c.exact.get(norm(v)) || (lang ? translateDates(v, lang) : v);
};
function risky(en) {
  // two text holes with only space between them cannot be told apart: "{name} {from}" would split
  // a Summary at its first space (and translate its first word)
  const holes = [...en.matchAll(/\{([a-zA-Z0-9_]+)\}/g)];
  for (let i = 1; i < holes.length; i += 1) {
    const between = en.slice(holes[i - 1].index + holes[i - 1][0].length, holes[i].index);
    if (!between.trim() && isTextHole(holes[i - 1][1]) && isTextHole(holes[i][1])) return true;
  }
  const words = en.replace(/\{[a-zA-Z0-9_]+\}/g, ' ').split(/[^A-Za-zÀ-ÿ]+/).filter(Boolean).length;
  if (words >= 2) return false;
  const bare = en.replace(/^[^A-Za-z{]+|[^A-Za-z}]+$/g, '');
  const at = (m) => m && isTextHole(m[1]);
  // "Status: {x}", "jobs · {x}", "{x} · open" are a label and its value — the colon or the dot says
  // so (a sentence someone wrote does not read that way); fine. The dot only beside a word of the
  // pattern's own: "{x} · {y}" would rewrite any text with a dot in it.
  const dot = words >= 1;
  return (at(bare.match(/^\{([a-zA-Z0-9_]+)\}/)) && !(dot && /^\{[a-zA-Z0-9_]+\}\s*·/.test(bare)))
    || (at(bare.match(/\{([a-zA-Z0-9_]+)\}$/)) && !/:\s*\{[a-zA-Z0-9_]+\}$/.test(bare) && !(dot && /·\s*\{[a-zA-Z0-9_]+\}$/.test(bare)));
}

// layers: [{ english: translation }, …] highest priority first, for ONE language.
// A key with {holes} is a pattern: "{n} work items" → "{n} werkitems".
export function compile(layers) {
  const exact = new Map(); const lower = new Map(); const patterns = []; const seen = new Set();
  for (const layer of layers || []) {
    if (!layer || typeof layer !== 'object') continue;
    for (const [en0, tr0] of Object.entries(layer)) {
      const en = norm(en0); const tr = norm(tr0);
      if (!en || !tr || seen.has(en)) continue;
      seen.add(en);
      if (HOLE.test(en)) {
        HOLE.lastIndex = 0;
        if (risky(en)) continue;
        const names = [];
        const re = new RegExp(`^${esc(en).replace(/\\\{([a-zA-Z0-9_]+)\\\}/g, (m, name) => { names.push(name); return holeRe(name); })}$`);
        // the words before the first hole and after the last: a text that does not start and end
        // with them is never tried against this pattern (a big grid has thousands of data cells)
        const head = en.slice(0, en.indexOf('{')); const tail = en.slice(en.lastIndexOf('}') + 1);
        // its rank: the length of its OWN words (a hole's name says nothing), and on a tie the one
        // whose holes are all typed ("{n} work items" before "{project} work items")
        const len = en.replace(/\{[a-zA-Z0-9_]+\}/g, '').length;
        patterns.push({ re, names, out: tr, len, typed: names.every((n) => !isTextHole(n)) ? 1 : 0, head, tail });
      } else {
        if (isDateWordAlone(en)) continue; // "Jan", "May", "Wed" alone may be a name
        exact.set(en, tr);
        const lc = en.toLowerCase();
        if (!lower.has(lc)) lower.set(lc, tr);
      }
      HOLE.lastIndex = 0;
    }
  }
  // the longest pattern wins ("{n} open work items" before "{n} {x}"). To keep a big page fast, a
  // pattern that starts with words is filed under their first letter, one that starts with a hole
  // under the last letter of its closing words, the rest (holes at both ends) on their own; lookup
  // walks the three lists together in this order, so a text meets only the patterns it could
  // match, still longest first
  patterns.sort((a, b) => (b.len - a.len) || (b.typed - a.typed));
  const byHead = new Map(); const byTail = new Map(); const open = [];
  const file = (map, k, p) => { if (!map.has(k)) map.set(k, []); map.get(k).push(p); };
  for (let i = 0; i < patterns.length; i += 1) {
    const p = patterns[i];
    p.idx = i; p.headL = p.head.toLowerCase(); p.tailL = p.tail.toLowerCase();
    if (p.head) file(byHead, p.headL[0], p);
    else if (p.tail) file(byTail, p.tailL[p.tailL.length - 1], p);
    else open.push(p);
  }
  return { exact, lower, patterns, byHead, byTail, open, size: exact.size + patterns.length };
}

const fill = (out, names, m) => out.replace(HOLE, (h, name) => { const i = names.indexOf(name); return i >= 0 ? m[i + 1] : h; });
const isUpper = (s) => /[A-Z]/.test(s) && s === s.toUpperCase();

function lookup(core, c, lang) {
  if (c.exact.has(core)) return c.exact.get(core);
  // a heading written in capitals in the code ("DUE DATE") — the same words, shouted
  if (isUpper(core)) { const t = c.lower.get(core.toLowerCase()); if (t) return t.toUpperCase(); }
  // the same words with another first letter ("open" / "Open")
  const t = c.lower.get(core.toLowerCase());
  if (t && core.length > 2) return core[0] === core[0].toUpperCase() ? t[0].toUpperCase() + t.slice(1) : t[0].toLowerCase() + t.slice(1);
  const lc = core.toLowerCase();
  const tryOne = (p) => {
    if ((p.headL && !lc.startsWith(p.headL)) || (p.tailL && !lc.endsWith(p.tailL))) return null;
    const m = core.match(p.re);
    if (!m) return null;
    // a hole that is itself a known phrase, or a date, is translated too ("Status: {x}" →
    // "Status: Oop"; "Due {date}" → "Sperdatum 16 Okt") — unless it carries people's text
    const parts = m.slice(1).map((v, i) => holeValue(v, p.names[i], c, lang));
    return fill(p.out, p.names, [m[0], ...parts]);
  };
  // the three lists this text could match, walked together in the patterns' own order
  const lists = [c.byHead.get(lc[0]) || [], c.byTail.get(lc[lc.length - 1]) || [], c.open];
  const at = [0, 0, 0];
  for (;;) {
    let k = -1;
    for (let x = 0; x < 3; x += 1) if (at[x] < lists[x].length && (k < 0 || lists[x][at[x]].idx < lists[k][at[k]].idx)) k = x;
    if (k < 0) return null;
    const r = tryOne(lists[k][at[k]]); at[k] += 1;
    if (r != null) return r;
  }
}
// for the tests: the same answer by trying EVERY pattern in order (what the lists above must equal)
export function _lookupByScan(text, c, lang) {
  const core = norm(text); const lc = core.toLowerCase();
  for (const p of c.patterns) {
    if ((p.headL && !lc.startsWith(p.headL)) || (p.tailL && !lc.endsWith(p.tailL))) continue;
    const m = core.match(p.re);
    if (m) return fill(p.out, p.names, [m[0], ...m.slice(1).map((v, i) => holeValue(v, p.names[i], c, lang))]);
  }
  return null;
}
export const _lookup = (text, c, lang) => lookup(norm(text), c, lang);

// What a rendered text reads in the language, or null when the dictionary does
// not know it. Leading / trailing space is kept, and so is punctuation round
// the words ("Due date:", "+ Add", "(optional)", "Loading…").
export function translate(text, c, lang) {
  if (!c || text == null) return null;
  const raw = String(text);
  if (!/[A-Za-z]/.test(raw)) return null;
  const lead = raw.match(/^\s*/)[0]; const tail = raw.match(/\s*$/)[0];
  const body = norm(raw);
  if (!body || body.length > MAX_LEN) return null;
  let t = lookup(body, c, lang);
  if (t == null) {
    const m = body.match(/^([•·+\-–—(“"'«‹*#]*\s*)(.*?)(\s*[:：…?!.,;*)”"'»›·]*)$/);
    if (m && m[2] && (m[1] || m[3])) { const mid = lookup(m[2], c, lang); if (mid != null) t = `${m[1]}${mid}${m[3]}`; }
  }
  if (t == null && lang) {
    const d = translateDates(body, lang);
    if (d !== body) t = d;
  }
  return t == null ? null : `${lead}${t}${tail}`;
}

// ---- dates: month and weekday names, only beside a date ------------------------------------------
const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const EN_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const DATE_WORDS = {
  af: {
    months: ['Januarie', 'Februarie', 'Maart', 'April', 'Mei', 'Junie', 'Julie', 'Augustus', 'September', 'Oktober', 'November', 'Desember'],
    short: ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'],
    days: ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrydag', 'Saterdag', 'Sondag'],
    dshort: ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Sa', 'So'],
  },
  de: {
    months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
    short: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
    days: ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'],
    dshort: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
  },
  pl: {
    months: ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec', 'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'],
    short: ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'],
    days: ['poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota', 'niedziela'],
    dshort: ['pon', 'wt', 'śr', 'czw', 'pt', 'sob', 'niedz'],
  },
};
const WORDS = (() => {
  const out = {};
  for (const [lang, w] of Object.entries(DATE_WORDS)) {
    const m = new Map();
    EN_MONTHS.forEach((name, i) => { m.set(name, w.months[i]); m.set(name.slice(0, 3), w.short[i]); m.set(`${name.slice(0, 3)}.`, `${w.short[i]}.`); });
    m.set('Sept', w.short[8]);
    EN_DAYS.forEach((name, i) => { m.set(name, w.days[i]); m.set(name.slice(0, 3), w.dshort[i]); });
    m.set('Tues', w.dshort[1]); m.set('Thur', w.dshort[3]); m.set('Thurs', w.dshort[3]);
    out[lang] = m;
  }
  return out;
})();
const DATE_RE = new RegExp(`\\b(${[...EN_MONTHS, ...EN_DAYS, ...EN_MONTHS.map((x) => x.slice(0, 3)), ...EN_DAYS.map((x) => x.slice(0, 3)), 'Sept', 'Tues', 'Thurs', 'Thur'].sort((a, b) => b.length - a.length).join('|')})\\b\\.?`, 'g');
// A text that IS a date — "16 Oct 2026", "Mon 12 Oct", "Oct 2026", "Oct 12, 2026", "Wed, 14 Oct
// 2026, 09:30", "W42 · 12 Oct" — and nothing else. A Summary "Delivery 12 Mar" or a person
// called "May" or "Jan" is not a date and stays as it is.
const MON = '(?:January|February|March|April|May|June|July|August|September|October|November|December|Sept|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\\.?';
const DAY = '(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday|Tues|Thurs|Thur|Mon|Tue|Wed|Thu|Fri|Sat|Sun)\\.?';
const TIME = '(?:,?\\s+\\d{1,2}:\\d{2}(?::\\d{2})?(?:\\s?[AaPp][Mm])?)?';
const DATE_CORE = `(?:W\\d{1,2}\\s*[·\\-–]\\s*)?(?:${DAY},?\\s+)?(?:\\d{1,2}\\s+${MON}(?:,?\\s+\\d{2,4})?|${MON}\\s+\\d{1,2}(?:,?\\s+\\d{4})?|${MON}\\s+\\d{4})${TIME}`;
// a date, or a range of two ("16 Jul 2026 – 16 Dec 2026", "12 Jul – 18 Jul")
const DATE_EXPR = new RegExp(`^${DATE_CORE}(?:\\s*[–—-]\\s*${DATE_CORE})?$`);

export function translateDates(text, lang) {
  const map = WORDS[lang];
  if (!map) return text;
  const s = String(text);
  if (!DATE_EXPR.test(s.trim())) return s;
  return s.replace(DATE_RE, (w) => map.get(w) || (w.endsWith('.') && map.get(w.slice(0, -1)) ? `${map.get(w.slice(0, -1))}.` : w));
}
// a month or weekday name by itself ("Jan", "May", "Wed") — never a dictionary phrase: it may be a name
export const isDateWordAlone = (s) => { const t = norm(s).replace(/\.$/, ''); return WORDS.af.has(t) || t === 'Sept'; };

// ---- what is never translated ---------------------------------------------------------------------
// A text that is data, not words for people: a key, a number, a date alone, an e-mail, a link, code.
export function looksLikeData(s) {
  const t = norm(s);
  if (!t) return true;
  if (!/[A-Za-z]/.test(t)) return true;
  if (/^[A-Z][A-Z0-9_]+-\d+$/.test(t)) return true;               // a work item key
  if (/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(t)) return true;           // an e-mail
  if (/^(https?:\/\/|www\.)/i.test(t)) return true;                // a link
  if (/^[\w-]+(\.[\w-]+)+\(?/.test(t) && !/\s/.test(t)) return true; // file.ext, a.b.c
  return false;
}

// Where nothing is translated: what people type or edit, code and queries, what people wrote
// (Summaries, comments, labels, catalogue names, whiteboard text), anything they can type back in
// (a translated label typed into a label box would SAVE the translation), and anything marked
// translate="no" / class="notranslate" (the web's own markers). The classes below belong to other
// modules — tools/brain coupling translator-keeps-out.
// (a <select> is read: its <option>s are, when each has its own value — see the engine's ownSkip)
export const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'INPUT', 'CODE', 'PRE', 'KBD', 'SAMP', 'TEMPLATE', 'IFRAME', 'CANVAS', 'VIDEO', 'AUDIO']);
// A class matches itself and its "-" family ('zqs' covers zqs-mirror, zqs-input …).
export const SKIP_CLASSES = [
  'notranslate',
  // editors: rich text (a translated word there would be SAVED), code, formulas, e-mail bodies
  'ProseMirror', 'tiptap', 'rich-text-editor', 'zsd-rte-body', 'zhtml-content', 'monaco-editor', 'cm-editor', 'CodeMirror', 'formula-field', 'wb-text-edit',
  // queries: every token of a ZQL box must stay where the caret thinks it is; the values a search
  // box filters (it searches the English names)
  'zqs', 'zql-box', 'bsb-list',
  // what people wrote: Summaries (work item screen, grids, pickers, references), comments, tab
  // titles (key + Summary)
  'screen-panel-summary', 'screen-parent-value-summary', 'comment-body', 'comment-add-editor', 'kb-card-summary', 'kb-strip-summary', 'jql-summary-text', 'jql-inline-input', 'tab-title', 'rte-mention',
  'wip-tree-cell', 'issue-summary', 'summary-text-link', 'issue-picker-option-summary', 'wiref-summary',
  // what people can type back in: labels and their suggestions, catalogue / line-item names, the
  // document as it will print (the PDF stays English too)
  'field-editor-tag', 'field-editor-option-label', 'li-card-name', 'li-opt', 'li-bold', 'li-paper',
  // the workflow diagram: its boxes are sized to the English status names
  'zsc-wfd-wrap',
];
export const TRANSLATED_ATTRS = ['placeholder', 'title', 'aria-label', 'alt', 'data-placeholder'];
